import React, { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { Box, Button, Divider, IconCaretDown, IconCaretRight, Typography, Wrapper } from '@screentone/core';

import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { DND_GROUP_ID_PREFIX, DND_TYPE_GROUP } from 'utils/layoutUtils';
import { AddGroupButton } from './AddGroupButton';
import styles from './LayoutModules.module.scss';
import { ModuleGroup } from './ModuleGroup';

const HelpSection = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box margin={{ bottom: 'md' }}>
      <Button
        tertiary
        fullWidth
        icon={open ? IconCaretDown : IconCaretRight}
        onClick={() => setOpen((v) => !v)}
        className={styles.fullWidthButton}
      >
        <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
          How this editor works
        </Typography>
      </Button>
      {open && (
        <Wrapper padding={{ horizontal: 'md', bottom: 'md' }}>
          <Divider margin={{ bottom: 'md' }} />
          <Typography variant="bodytext" margin={{ bottom: 'sm' }}>
            <strong>This is a structural template, not live content.</strong> A layout defines the groups and modules
            that make up a page. When an editor creates a new page they pick a layout as a starting point — after that
            the page is independent, so changes here do not affect existing pages.
          </Typography>
          <Typography variant="bodytext" margin={{ bottom: 'sm' }}>
            <strong>Groups</strong> are named sections (e.g. &ldquo;Top Stories&rdquo;, &ldquo;Right Rail&rdquo;). Each
            group has its own module count limits (min/max), per-treatment-type limits, and controls for page-editor
            behaviour (toggleable on/off, show module count, etc.). Configure these via the Group Options menu on each
            group header.
          </Typography>
          <Typography variant="bodytext" margin={{ bottom: 'sm' }}>
            <strong>Modules</strong> are the individual content slots inside a group. Each module has a{' '}
            <strong>treatment type</strong> that determines how content is displayed — article count, layout, image
            handling. Treatment types and their defaults are configured in Publication Settings &rarr; Treatment Types.
          </Typography>
          <Typography variant="bodytext" margin={{ bottom: 'sm' }}>
            <strong>Allowed treatment types</strong> on a group controls which treatment types page editors can add when
            editing a page. Note that even if a type is listed here, page editors cannot add more of it if the
            per-treatment-type max is already reached. As a layout author, you can add any treatment type from the
            publication catalog to any group regardless of this setting.
          </Typography>
          <Typography variant="bodytext" margin={{ bottom: 'sm' }}>
            <strong>Per-treatment-type limits</strong> set the minimum and maximum number of modules of each type within
            a group. Min and max are required for every treatment type that has modules — save is blocked until all are
            set.
          </Typography>
          <Typography variant="bodytext" margin={{ bottom: 'none' }}>
            <strong>Module Options</strong> (&hellip; menu on each module) lets you override display fields — title,
            subtitle, item counts — on top of the treatment type defaults.
          </Typography>
        </Wrapper>
      )}
    </Box>
  );
};

export const LayoutModules = () => {
  const { layout, modules, reorderModules, reorderGroups, treatmentTypeSettings } = useStructureEditContext();

  const layoutGroups = useMemo(() => layout.structure?.groups ?? [], [layout.structure?.groups]);

  const modulesByGroup = useMemo(() => {
    const map = new Map<string, typeof modules>();
    layoutGroups.forEach((g) => map.set(g.id, []));
    modules.forEach((m) => {
      const gid = m.groupId;
      if (map.has(gid)) map.get(gid)!.push(m);
    });
    return map;
  }, [modules, layoutGroups]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, type } = result;
      if (!destination) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index) return;

      if (type === DND_TYPE_GROUP) {
        const groups = [...layoutGroups];
        const [removed] = groups.splice(source.index, 1);
        groups.splice(destination.index, 0, removed);
        reorderGroups(groups);
        return;
      }

      const sourceGroupId = source.droppableId.replace(DND_GROUP_ID_PREFIX, '');
      const destGroupId = destination.droppableId.replace(DND_GROUP_ID_PREFIX, '');
      if (sourceGroupId !== destGroupId) return;

      const groupModules = modulesByGroup.get(sourceGroupId) ?? [];
      if (groupModules.length === 0) return;

      const sourceModule = groupModules[source.index];
      const destModule = groupModules[destination.index];
      const allModules = [...modules];
      const sourceIdx = allModules.findIndex((m) => m === sourceModule);
      const destIdx = allModules.findIndex((m) => m === destModule);
      if (sourceIdx === -1 || destIdx === -1) return;

      const [removed] = allModules.splice(sourceIdx, 1);
      allModules.splice(destIdx, 0, removed);
      reorderModules(allModules);
    },
    [modulesByGroup, modules, reorderModules, reorderGroups, layoutGroups]
  );

  const availableTreatmentTypes = useMemo(
    () =>
      treatmentTypeSettings.length > 0
        ? treatmentTypeSettings.map((s) => s.treatmentTypeKey ?? s.treatmentType)
        : (layout.availableTreatmentTypes ?? []),
    [treatmentTypeSettings, layout.availableTreatmentTypes]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Wrapper padding={{ all: 'md' }}>
        <HelpSection />
        <Typography variant="header3" margin={{ bottom: 'sm' }}>
          Groups
        </Typography>
        <Droppable droppableId="layout-groups" type={DND_TYPE_GROUP}>
          {(provided) => (
            <div className={styles.moduleList} ref={provided.innerRef} {...provided.droppableProps}>
              {layoutGroups.length === 0 && (
                <Wrapper padding={{ vertical: 'md' }}>
                  <Typography variant="note" color="asphalt" align="center">
                    No groups configured. Use the button below to add one.
                  </Typography>
                </Wrapper>
              )}
              <AddGroupButton insertAfterGroupId={null} />
              {Array.from(modulesByGroup.entries()).map(([groupId, groupModules], index) => {
                const layoutGroup = layoutGroups.find((g) => g.id === groupId);
                return (
                  <React.Fragment key={groupId}>
                    <ModuleGroup
                      index={index}
                      totalGroups={modulesByGroup.size}
                      groupId={groupId}
                      groupLabel={layoutGroup?.config.label}
                      layoutGroup={layoutGroup}
                      modules={groupModules}
                      availableTreatmentTypes={availableTreatmentTypes}
                    />
                    <AddGroupButton insertAfterGroupId={groupId} />
                  </React.Fragment>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </Wrapper>
    </DragDropContext>
  );
};
